/**
 * The arena exports, packed for the wire.
 *
 * The Blender exports are JSON triangle soup, 5–14 MB of decimal text per
 * arena. Gzip makes the download tolerable, but a phone still has to
 * `JSON.parse` all of it and copy every number into a typed array on the main
 * thread, while the match is already running. This is the same data as bytes:
 *
 *   'CAB1' | u32 header length | header JSON (padded to 4) | batch data
 *
 * The header is the asset with each batch's `positions`/`normals` replaced by
 * `count` (floats per array). Per batch the data is `count` float32 positions,
 * then `count` int8 normals (x127), padded to 4. Positions end up float32 on
 * the GPU either way, so they are lossless; normals lose less than a degree.
 *
 * The JSON stays the source of truth (the review tools and tests read it);
 * `scripts/pack-arenas.mjs` writes the `.bin` beside it at build time, and the
 * loader falls back to the JSON when the `.bin` is missing.
 */
const MAGIC = 0x31424143; // 'CAB1', little-endian
const pad4 = (n) => (n + 3) & ~3;

export function encodeArenaBinary(asset) {
  const batches = asset.batches.map(({ positions, normals, ...meta }) => {
    if (normals.length !== positions.length) throw new Error(`${meta.partId}: normals/positions length mismatch`);
    return { ...meta, count: positions.length };
  });
  const header = new TextEncoder().encode(JSON.stringify({ ...asset, batches }));
  let size = 8 + pad4(header.length);
  for (const b of batches) size += b.count * 4 + pad4(b.count);
  const buffer = new ArrayBuffer(size);
  const view = new DataView(buffer);
  view.setUint32(0, MAGIC, true);
  view.setUint32(4, header.length, true);
  new Uint8Array(buffer, 8, header.length).set(header);
  let offset = 8 + pad4(header.length);
  asset.batches.forEach(({ positions, normals }) => {
    new Float32Array(buffer, offset, positions.length).set(positions);
    offset += positions.length * 4;
    const n = new Int8Array(buffer, offset, normals.length);
    for (let i = 0; i < normals.length; i++) n[i] = Math.max(-127, Math.min(127, Math.round(normals[i] * 127)));
    offset += pad4(normals.length);
  });
  return new Uint8Array(buffer);
}

/** The asset shape the batch builders take, with Float32Array arrays. Null if not ours. */
export function decodeArenaBinary(buffer) {
  if (buffer.byteLength < 8) return null;
  const view = new DataView(buffer);
  if (view.getUint32(0, true) !== MAGIC) return null;
  const headerLength = view.getUint32(4, true);
  const asset = JSON.parse(new TextDecoder().decode(new Uint8Array(buffer, 8, headerLength)));
  let offset = 8 + pad4(headerLength);
  asset.batches = asset.batches.map(({ count, ...meta }) => {
    // Views, not copies: the geometry builders copy into their own attribute.
    const positions = new Float32Array(buffer, offset, count);
    offset += count * 4;
    const packed = new Int8Array(buffer, offset, count);
    offset += pad4(count);
    const normals = new Float32Array(count);
    for (let i = 0; i < count; i++) normals[i] = packed[i] / 127;
    return { ...meta, positions, normals };
  });
  if (offset !== buffer.byteLength) throw new Error('Arena binary length mismatch');
  return asset;
}

/** `.bin` beside the `.json` when the build packed one, else the JSON itself. */
export async function fetchArenaAsset(jsonUrl, fetchImpl = fetch) {
  const binUrl = jsonUrl.replace(/\.json(\?|$)/, '.bin$1');
  if (binUrl !== jsonUrl) {
    try {
      const response = await fetchImpl(binUrl);
      if (response.ok) {
        const asset = decodeArenaBinary(await response.arrayBuffer());
        if (asset) return asset;
      }
    } catch {
      // Missing or unreadable: the JSON is always there.
    }
  }
  const response = await fetchImpl(jsonUrl);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}
